import { useEffect, useState } from 'react';
import { Play } from 'lucide-react';
import Button from '../ui/Button';
import { errorMessage } from '../../utils/apiResponse';

/**
 * Plays a recording or a voicemail. The audio route needs the bearer token,
 * so the file is fetched as a blob on "Play" (never on page load: a page of
 * voicemails would download every one) and handed to an <audio> element.
 */
export default function AudioPlayback({ load, label }: { load: () => Promise<Blob>; label: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const play = async () => {
    setBusy(true);
    setError(null);
    try {
      setUrl(URL.createObjectURL(await load()));
    } catch (err) {
      setError(errorMessage(err, 'Could not load the audio'));
    } finally {
      setBusy(false);
    }
  };

  if (url) {
    return (
      // A phone call has no caption track; its transcript, where there is one, sits beside it.
      <audio controls autoPlay src={url} className="w-full max-w-md" aria-label={label} />
    );
  }
  return (
    <div className="space-y-1">
      <Button size="sm" variant="outline" onClick={play} isLoading={busy}>
        <Play className="h-4 w-4 mr-1" aria-hidden />{label}
      </Button>
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
    </div>
  );
}
